import DataQuery from 'data/DataQuery';
import { Contract_CapacityElectricity } from 'data/endpoints';
import { useQuery } from 'data/hooks/useQuery';
import React from 'react';
import CapacityElectricityDTO from "features/contracts/dtos/CapacityElectricityDTO";
import ReservedCapacityRow from 'features/contracts/components/reservedCapacity/ReservedCapacityRow';
import ReservedCapacityElectricity from 'features/contracts/components/reservedCapacity/ReservedCapacityElectricity';
import ReservedCapacityElectricityHeader from 'features/contracts/components/reservedCapacity/ReservedCapacityElectricityHeader';
import NoContentWrapper from 'features/shared/components/noContentWrapper/NoContentWrapper';
import { groupBy } from 'features/shared/helpers/Arrays';
import { CapacityType } from '../enums/capacityType';
import { useTranslation } from 'react-i18next';
import ReservedCapacityElectricityDraftYearly from 'features/contracts/components/reservedCapacity/ReservedCapacityElectricityDraftYearly';
import ReservedCapacityElectricityDraftYearlyHeader from 'features/contracts/components/reservedCapacity/ReservedCapacityElectricityDraftYearlyHeader';

type ElectricityCapacityType = 'Yearly' | 'Monthly';

type Props = {
  contractId: string;
  year: number;
}

const CapacityElectricitySection = ({ contractId, year }: Props) => {
  // translations
  const tLabels = useTranslation("contracts\\contracts").t;

  // fetch data
  const capacityQuery: DataQuery = {
    url: Contract_CapacityElectricity,
    routeParams: new URLSearchParams([["contractId", contractId!], ["year", String(year)]])
  };
  const { data: capacities, loadStatus } = useQuery<CapacityElectricityDTO[]>(
    capacityQuery,
    [],
    [contractId, year]
  );

  // helpers
  const getTypeOfDraftCapacities = (capacityGroups: Record<string, CapacityElectricityDTO[]>): ElectricityCapacityType => { 
    let draftCapacities = capacityGroups[CapacityType.Draft];
    let validCapacities = capacityGroups[CapacityType.Valid];

    let firstDraftCapacity = draftCapacities[0];
    let validCapacityInSameMonth = validCapacities.find(x => x.month == firstDraftCapacity.month);

    if (validCapacityInSameMonth && firstDraftCapacity.yearlyCapacity != validCapacityInSameMonth.yearlyCapacity) {
      return "Yearly";
    } else {
      return "Monthly";
    }
  }

  // sub-renders
  const renderCapacities = (items: CapacityElectricityDTO[]) => {
    return items.map((item, index) => (
      <ReservedCapacityRow key={`${item.type}-${index}`}>
        <ReservedCapacityElectricity 
          capacity={item}
          showTypeLabel
        />
      </ReservedCapacityRow>
    ));
  }

  const renderCapacityGroups = () => {
    let groups = groupBy(capacities, "type") as Record<string, CapacityElectricityDTO[]>;
    let hasDraftCapacities = groups[CapacityType.Draft] !== undefined;
    let hasValidCapacities = groups[CapacityType.Valid] !== undefined;

    let result: JSX.Element[] = [];

    // draft capacities
    if (hasDraftCapacities) {
      let draftCapacitiesType = getTypeOfDraftCapacities(groups);
      // render drafts title
      renderTitle(result, tLabels(`capacityElectricitySection.draftCapacities.${draftCapacitiesType}`));
      // render draft capacities and header
      if (draftCapacitiesType == "Yearly") {
        renderYearlyHeader(result);

        let yearlyCapacity = renderYearlyDraftCapacities(groups[CapacityType.Draft]);
        result.push(yearlyCapacity);
      } else {
        renderHeader(result);

        let monthlyCapacities = renderMonthlyDraftCapacities(groups[CapacityType.Draft], groups[CapacityType.Valid]);
        result.push(...monthlyCapacities);
      }
    }

    // valid capacities
    if (hasValidCapacities) {
      let validCapacities = groups[CapacityType.Valid]
      // render valid capacities title (only if displayed drafts title)
      if (hasDraftCapacities) {
        renderTitle(result, tLabels("capacityElectricitySection.validValues"), true);
      }
      // render valid capacities and header
      renderHeader(result);
      
      let validCapacityElems = renderCapacities(validCapacities);
      result.push(...validCapacityElems);
    }

    return result;
  };

  const renderYearlyDraftCapacities = (capacities: CapacityElectricityDTO[]) => {
    return (
      <ReservedCapacityRow key={`yearly-capacity-${capacities[0].month}`}>
        <ReservedCapacityElectricityDraftYearly capacity={capacities[0]} />
      </ReservedCapacityRow>
    );
  }

  const renderMonthlyDraftCapacities = (draftCapacities: CapacityElectricityDTO[], validCapacities: CapacityElectricityDTO[]) => {
    if (validCapacities !== undefined) {
      // only values which have difference monhthly capacity (SET (minus) operation - draftCapacities\validCapacities)
      let differenceMonhtlyCapacities = draftCapacities.filter(
        (drafCapacity) =>
          !validCapacities.some(
            (validCapacity) => drafCapacity.month == validCapacity.month && drafCapacity.monthlyCapacity == validCapacity.monthlyCapacity
          )
      );
      return renderCapacities(differenceMonhtlyCapacities);
    } else {
      return renderCapacities(draftCapacities);
    }
  }

  const renderHeader = (result: JSX.Element[]) => { 
    let elem = <ReservedCapacityElectricityHeader showUnit />;
    result.push(elem);
  }

  const renderYearlyHeader = (result: JSX.Element[]) => { 
    let elem = <ReservedCapacityElectricityDraftYearlyHeader/>;
    result.push(elem);
  }

  const renderTitle = (result: JSX.Element[], title: string, topMargin: boolean = false ) => { 
    let titleElem = <h4 className={`text-muted${topMargin ? " mt-4" : ""}`}>{title}</h4>;
    result.push(titleElem);
  }

  // render
  return (
    <NoContentWrapper
      data={capacities}
      loadStatus={loadStatus}
      noContentAsAlert
      noContentInfo={tLabels("capacityElectricitySection.reservedCapacityNotFound")}
    >
      {renderCapacityGroups()}
    </NoContentWrapper>
  )
}

export default CapacityElectricitySection;